import { Button, TextField } from '@material-ui/core';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { ModalWrapper } from 'components/ModalWrapper';
import * as api from '../../api';
import { IProject } from '../../types';
import InlineConfirmDelete from '../shared/InlineConfirmDelete';

interface IProps {
  project: IProject;
  hasSessions: boolean;
  onClose: () => void;
  onSave: (project: IProject) => void;
  onDelete: (projectId: number) => void;
}

const ProjectSettingsModal: React.FC<IProps> = ({ project, hasSessions, onClose, onSave, onDelete }) => {
  const [name, setName] = useState(project.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await api.updateProject(project.id, { name });
      if (result.ok && result.data) {
        onSave(result.data);
      } else {
        setError(result.message || 'Failed to save');
      }
    } catch {
      setError('Failed to save project settings');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.deleteProject(project.id);
      onDelete(project.id);
    } catch {
      setError('Failed to delete project');
    }
  };

  const cancelDelete = useCallback(() => setConfirmingDelete(false), []);

  return (
    <ModalWrapper onClose={onClose} title={`Project Settings: ${project.name}`} width={450} height={250}>
      <Form>
        {error && <ErrorText>{error}</ErrorText>}
        <TextField
          label="Name"
          variant="outlined"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />
        {confirmingDelete && (
          <InlineConfirmDelete
            itemName={project.name}
            onConfirm={handleDelete}
            onCancel={cancelDelete}
          />
        )}
        <ButtonRow>
          {!hasSessions && !confirmingDelete && (
            <DeleteButton variant="outlined" onClick={() => setConfirmingDelete(true)}>
              Delete
            </DeleteButton>
          )}
          <Spacer />
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </ButtonRow>
      </Form>
    </ModalWrapper>
  );
};

export default ProjectSettingsModal;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const DeleteButton = styled(Button)`
  && {
    color: #e53935;
    border-color: #e53935;
  }
`;

const ErrorText = styled.div`
  color: #e53935;
  font-size: 13px;
`;
